import React, { useState, useEffect } from "react";
import {
  Box, Grid, TextField, Button, FormControlLabel, Switch,
  Typography, Divider, CircularProgress, Chip, Alert,
  RadioGroup, Radio, FormControl, FormLabel, InputLabel, Select, MenuItem,
} from "@mui/material";
import Swal from "sweetalert2";
import { StoreStation, PatchStation, SendStationDailyReportTest } from "api/stations";
import { GetWhatsAppGroupChats } from "api/parcelMonitoring";
import { getDefaultInstance } from "api/customerGroup";

const defaultForm = {
  id: "", name: "", nameLA: "", parcelStatus: "", message: "",
  googleMap: "", stationType: "", isFanshine: false,
  isPhotoRequired: false, isCourierLabelRequired: false, allowPackageCreation: false, requireHandOverPhotos: true, collectPayment: true, validflag: true,
  monitorDwellDays: "", monitorTransitDays: "", monitorUnassignedDays: "",
  dailyReportEnabled: false, dailyReportTime: "", dailyReportChatId: null, dailyReportChatName: null,
};

// Derive stationType from raw station flags
const getStationType = (data) => {
  if (data?.isSortStation) return "sort";
  if (data?.isTransitStation) return "transit";
  if (data?.finalStation) return "final";
  return "other";
};

// Convert stationType back to individual flags for the backend
const applyStationType = (form) => ({
  ...form,
  isSortStation: form.stationType === "sort",
  isTransitStation: form.stationType === "transit",
  finalStation: form.stationType === "final",
  // "other" means none of the above flags are set
});

// Empty string / blank = use the global default (see AppSetting
// "parcelMonitoring.config") — sent to the backend as null, not 0.
const numberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const StationForm = ({ editData, onSaved, onCancel }) => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const isEdit = !!editData;

  // Daily report's WhatsApp group picker — always the default instance (never
  // per-station), same instance OTP/contact-form/parcel-monitor digests use.
  const [defaultGroup, setDefaultGroup] = useState(undefined); // undefined = loading, null = none set
  const [groupChats, setGroupChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatsError, setChatsError] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await getDefaultInstance();
      setDefaultGroup(res.success ? res.data?.defaultGroup || null : null);
    })();
  }, []);

  useEffect(() => {
    if (!defaultGroup?.id) { setGroupChats([]); return; }
    setLoadingChats(true);
    setChatsError(null);
    GetWhatsAppGroupChats(defaultGroup.id).then((res) => {
      setLoadingChats(false);
      if (res.success) setGroupChats(res.data || []);
      else { setGroupChats([]); setChatsError(res.message || "Failed to load groups"); }
    });
  }, [defaultGroup?.id]);

  useEffect(() => {
    if (editData) {
      setForm({
        ...defaultForm,
        ...editData,
        stationType: getStationType(editData),
        // null (unset — use global default) must become "" for a controlled TextField.
        monitorDwellDays: editData.monitorDwellDays ?? "",
        monitorTransitDays: editData.monitorTransitDays ?? "",
        monitorUnassignedDays: editData.monitorUnassignedDays ?? "",
        dailyReportTime: editData.dailyReportTime ?? "",
      });
    } else {
      setForm(defaultForm);
    }
  }, [editData]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const sendTest = async () => {
    setTesting(true);
    const res = await SendStationDailyReportTest(editData.id);
    setTesting(false);
    Swal.fire({
      icon: res.success ? "success" : "error",
      text: res.success ? "Test report queued — check the group in WhatsApp" : res.message || "Failed to send",
      timer: 2500, toast: true, position: "top-right", showConfirmButton: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.stationType) {
      Swal.fire({ title: "Station Type Required", icon: "warning", text: "Please select a station type: Sort, Transit, or Final." });
      return;
    }
    setLoading(true);
    const payload = {
      ...applyStationType(form),
      monitorDwellDays: numberOrNull(form.monitorDwellDays),
      monitorTransitDays: numberOrNull(form.monitorTransitDays),
      monitorUnassignedDays: numberOrNull(form.monitorUnassignedDays),
      dailyReportTime: form.dailyReportTime || null,
    };
    const res = isEdit ? await PatchStation(payload) : await StoreStation(payload);
    setLoading(false);
    if (res.success) {
      Swal.fire({ title: isEdit ? "Updated!" : "Created!", icon: "success", timer: 1500, showConfirmButton: false });
      if (onSaved) onSaved();
      if (!isEdit) setForm(defaultForm);
    } else {
      Swal.fire({ title: "Error", icon: "error", text: res.message });
    }
  };

  // Final stations run the WhatsApp pickup/delivery hand-over; they can opt out of
  // hand-over photos / money collection there (e.g. partner stations that settle themselves).
  const finalStationOptions = [
    {
      key: "requireHandOverPhotos",
      label: "Require Hand-over Photos",
      help: "Staff must attach hand-over photos on the WhatsApp pickup/delivery screen. Off = photos are optional.",
    },
    {
      key: "collectPayment",
      label: "Collect Payment at Hand-over",
      help: "Off = hand-over only. The station settles the money with the customer itself, so nothing is charged or recorded.",
    },
  ];

  const otherFlags = [
    { key: "isFanshine", label: "Fanshine" },
    { key: "isPhotoRequired", label: "Photo Required" },
    { key: "isCourierLabelRequired", label: "Courier Label Required" },
    { key: "allowPackageCreation", label: "Allow New Package Creation" },
    { key: "validflag", label: "Active" },
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, pt: 1 }}>
      <Typography variant="h6" gutterBottom>
        {isEdit ? "Edit Station" : "New Station"}
        {isEdit && (
          <Chip label={form.validflag ? "Active" : "Inactive"} color={form.validflag ? "success" : "default"} size="small" sx={{ ml: 1 }} />
        )}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Station ID *" value={form.id} onChange={handleChange("id")} disabled={isEdit} required size="small" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Name (English) *" value={form.name} onChange={handleChange("name")} required size="small" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Name (Lao) *" value={form.nameLA} onChange={handleChange("nameLA")} required size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Parcel Status</InputLabel>
            <Select value={form.parcelStatus} label="Parcel Status" onChange={handleChange("parcelStatus")}>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="In-transit">In-transit</MenuItem>
              <MenuItem value="At Hub">At Hub</MenuItem>
              <MenuItem value="Arrived">Arrived</MenuItem>
              <MenuItem value="Delivered">Delivered</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Google Map URL" value={form.googleMap} onChange={handleChange("googleMap")} size="small" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth multiline rows={2} label="Message (WhatsApp notification text)" value={form.message} onChange={handleChange("message")} size="small" />
        </Grid>

        {/* Station Type — mutually exclusive */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <FormControl required>
            <FormLabel sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5 }}>
              Station Type *
            </FormLabel>
            <RadioGroup row value={form.stationType} onChange={handleChange("stationType")}>
              <FormControlLabel value="sort" control={<Radio color="secondary" size="small" />} label="Sort Station" />
              <FormControlLabel value="transit" control={<Radio color="secondary" size="small" />} label="Transit Station" />
              <FormControlLabel value="final" control={<Radio color="secondary" size="small" />} label="Final Station" />
              <FormControlLabel value="other" control={<Radio color="secondary" size="small" />} label="Other" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Parcel monitoring dwell thresholds */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Parcel Monitoring Thresholds (business days, Sundays excluded)
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth type="number" label="Dwell alert (days)" value={form.monitorDwellDays}
            onChange={handleChange("monitorDwellDays")} size="small"
            helperText="Sitting at this station too long. Blank = use global default."
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth type="number" label="Transit alert (days)" value={form.monitorTransitDays}
            onChange={handleChange("monitorTransitDays")} size="small"
            helperText="Departed from here, never arrival-scanned. Blank = use global default."
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth type="number" label="Unassigned alert (days)" value={form.monitorUnassignedDays}
            onChange={handleChange("monitorUnassignedDays")} size="small"
            helperText="Arrived with no route assigned yet (set this on the sort station, e.g. VTE). Blank = use global default."
            inputProps={{ min: 1 }}
          />
        </Grid>

        {/* Branch cash daily report — sent as an image to this station's WhatsApp
            group, always through the default WhatsApp instance. */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Branch Cash Daily Report (WhatsApp)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={!!form.dailyReportEnabled} onChange={handleChange("dailyReportEnabled")} color="secondary" size="small" />}
            label="Send daily cash report to WhatsApp"
          />
        </Grid>
        {form.dailyReportEnabled && (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth type="time" label="Send at" value={form.dailyReportTime}
                onChange={handleChange("dailyReportTime")} size="small"
                InputLabelProps={{ shrink: true }} helperText="24h, Asia/Bangkok time"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth size="small" disabled={!defaultGroup?.id || loadingChats}>
                <InputLabel>WhatsApp group</InputLabel>
                <Select
                  label="WhatsApp group"
                  value={form.dailyReportChatId || ""}
                  onChange={(e) => {
                    const chat = groupChats.find((c) => c.id === e.target.value);
                    setForm((prev) => ({ ...prev, dailyReportChatId: e.target.value, dailyReportChatName: chat?.name || null }));
                  }}
                >
                  {groupChats.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {defaultGroup === null && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No default WhatsApp instance is set — configure one in WhatsApp Settings first.
                </Alert>
              )}
              {loadingChats && <Typography variant="caption" color="text.secondary">Loading groups…</Typography>}
              {!loadingChats && defaultGroup?.id && chatsError && (
                <Alert severity="error" sx={{ mb: 1 }}>Couldn't load groups: {chatsError}</Alert>
              )}
              {!loadingChats && defaultGroup?.id && !chatsError && groupChats.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No groups found. Add the default instance's WhatsApp number to this branch's group first.
                </Alert>
              )}
              {isEdit && editData.dailyReportChatId && (
                <Button variant="outlined" color="secondary" size="small" disabled={testing} onClick={sendTest}>
                  {testing ? <CircularProgress size={16} color="inherit" /> : "Send test now"}
                </Button>
              )}
            </Grid>
          </>
        )}

        {/* Final-station group — options for the WhatsApp pickup/delivery hand-over */}
        {form.stationType === "final" && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ border: 1, borderColor: "secondary.main", borderRadius: 2, p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>Final Station — Pickup / Delivery</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                What staff must do when handing parcels over to the customer at this station.
              </Typography>
              {finalStationOptions.map(({ key, label, help }) => (
                <Box key={key} sx={{ mb: 0.5 }}>
                  <FormControlLabel
                    control={<Switch checked={form[key] !== false} onChange={handleChange(key)} color="secondary" size="small" />}
                    label={label}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 5.5, mt: -0.5 }}>
                    {help}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        {/* Other independent flags */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Other Options</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {otherFlags.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={<Switch checked={!!form[key]} onChange={handleChange(key)} color="secondary" size="small" />}
                label={label}
                sx={{ mr: 1 }}
              />
            ))}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" gap={1} justifyContent="flex-end" mt={1}>
            {onCancel && (
              <Button variant="outlined" color="secondary" onClick={onCancel} disabled={loading}>Cancel</Button>
            )}
            <Button type="submit" variant="contained" color="secondary" disabled={loading} startIcon={loading && <CircularProgress size={16} />}>
              {isEdit ? "Save Changes" : "Create Station"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StationForm;
